import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { FramedPane } from '@/components/FramedPane';

/** Load state of the list a [FramedSearchPane] renders. */
export type FramedSearchStatus = 'pending' | 'error' | 'empty' | 'ready';

/**
 * The left pane of a split-layout page (user management, customers, …): a framed card with a pinned
 * search box above a scrollable, horizontally-overflowing list, plus shared pending/error/empty states.
 * The caller maps its rows to `<li>` children (and any "loading more" footer), drives the search box via
 * `search`/`onSearchChange`, and reports which state to show via `status`.
 */
export function FramedSearchPane({
  search,
  onSearchChange,
  searchPlaceholder,
  listLabel,
  status,
  errorMessage,
  emptyMessage,
  children,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  listLabel: string;
  status: FramedSearchStatus;
  errorMessage: string;
  emptyMessage: string;
  children: ReactNode;
}) {
  return (
    <FramedPane className="gap-4">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-8"
          type="search"
          autoComplete="off"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={searchPlaceholder}
        />
      </div>

      {status === 'pending' ? (
        <Skeleton className="min-h-0 w-full flex-1" />
      ) : status === 'error' ? (
        <p className="text-sm text-destructive">{errorMessage}</p>
      ) : status === 'empty' ? (
        <p className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        <ScrollArea className="min-h-0 flex-1">
          <ul className="space-y-1" aria-label={listLabel}>
            {children}
          </ul>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      )}
    </FramedPane>
  );
}
